import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  FEATURE_DISABLED_EVENT,
  FEATURES_UPDATED_EVENT,
  FeatureMap,
  getAuthToken,
  getOrgFeatures,
  setOrgFeatures,
} from "../services/authService";
import { getMyProfile } from "../services/UsersService";
import FeatureLockedDialog from "../components/features/FeatureLockedDialog";

type FeaturesContextType = {
  features: FeatureMap;
  /**
   * Absent keys read as enabled — an organization with no feature rows is
   * all-enabled, and it also means nothing flashes locked before the map loads.
   */
  isEnabled: (feature?: string) => boolean;
  /** Opens the "what this module does / contact us" dialog. */
  showFeatureLocked: (feature: string) => void;
  refreshFeatures: () => Promise<void>;
};

const FeaturesContext = createContext<FeaturesContextType | undefined>(
  undefined
);

export const useFeatures = (): FeaturesContextType => {
  const ctx = useContext(FeaturesContext);
  if (!ctx) {
    throw new Error("useFeatures must be used within a FeaturesProvider");
  }
  return ctx;
};

export const FeaturesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [features, setFeatures] = useState<FeatureMap>(() => getOrgFeatures());
  const [lockedFeature, setLockedFeature] = useState<string | null>(null);

  const refreshFeatures = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const profile = await getMyProfile();
      if (profile?.features) {
        setFeatures(profile.features);
        setOrgFeatures(profile.features);
      }
    } catch (error) {
      // Keep whatever was cached at login.
      console.warn("Could not refresh feature switches", error);
    }
  }, []);

  // UserContext's own /auth/me call refreshes the cache on every load, so this
  // only fetches when there is nothing cached at all.
  useEffect(() => {
    if (Object.keys(getOrgFeatures()).length === 0) {
      refreshFeatures();
    }
  }, [refreshFeatures]);

  useEffect(() => {
    const handler = () => setFeatures(getOrgFeatures());
    window.addEventListener(FEATURES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(FEATURES_UPDATED_EVENT, handler);
  }, []);

  // A toggle can flip mid-session, so a 403 re-reads the map and the sidebar and
  // any FeatureRoute catch up in the same beat. It deliberately does not pop the
  // dialog — these fire from background fetches too, and an unprompted modal is
  // worse than the screen quietly locking itself.
  useEffect(() => {
    const handler = () => {
      refreshFeatures();
    };
    window.addEventListener(FEATURE_DISABLED_EVENT, handler);
    return () => window.removeEventListener(FEATURE_DISABLED_EVENT, handler);
  }, [refreshFeatures]);

  const isEnabled = useCallback(
    (feature?: string) => {
      if (!feature) return true;
      const value = features?.[feature];
      return value === undefined ? true : !!value;
    },
    [features]
  );

  const showFeatureLocked = useCallback((feature: string) => {
    setLockedFeature(feature);
  }, []);

  return (
    <FeaturesContext.Provider
      value={{ features, isEnabled, showFeatureLocked, refreshFeatures }}
    >
      {children}
      <FeatureLockedDialog
        open={!!lockedFeature}
        feature={lockedFeature}
        onClose={() => setLockedFeature(null)}
      />
    </FeaturesContext.Provider>
  );
};

export default FeaturesContext;
